import { useMemo, useState } from 'react'
import useRemoteDesktopControlPermissionActions from '@/services/useRemoteDesktopControlPermissionActions'
import { AnalyticsEvents } from '@/services/AnalyticsService'
import useAnalytics from '@/services/useAnalytics'
import { PortalModal } from '@/pages/PortalModal'
import useFloatingStatusBarWindowFit from './internal/useFloatingStatusBarWindowFit'
import InternalCard from './internal/InternalCard'
import InternalButton from './internal/InternalButton'
import CaretLeftIcon from '@/CustomIcons/CaretLeftIcon'
import CaretRightIcon from '@/CustomIcons/CaretRightIcon'
import {
    DesktopControllingCard,
    PresentingCard,
    RemoteRecorderPeerCard,
    RemoteRecordingPeerCard,
    ScreenRecordingCard,
    SessionRecordingCard,
} from './FloatingTopStatusBarCards'
import DragHandleIcon from '@/CustomIcons/DragHandleIcon'
import { env } from '@/services/EnvironmentVariables'

export function FloatingTopStatusBar({
    isPresenting,
    isScreenRecording,
    remoteRecordingPeer,
    remoteRecorderPeer,
    sessionRecordingPeer,
    isDesktopBeingControlled = false,
    debugDisplay,

    onClose,
    onEndPresentation,
    onEndScreenRecording,
    onEndRemoteRecording,
}: {
    isPresenting: boolean
    isScreenRecording: boolean | 'loading'
    remoteRecordingPeer: string | null
    remoteRecorderPeer: string | null
    sessionRecordingPeer: string | null
    isDesktopBeingControlled: boolean
    debugDisplay?: boolean

    onClose?: () => void
    onEndPresentation?: () => void
    onEndScreenRecording?: () => void
    onEndRemoteRecording?: () => void
}) {
    const { logUserActivityWithContext } = useAnalytics()

    const RemoteDesktopControlPermissions = useRemoteDesktopControlPermissionActions()

    const [isRetracted, setIsRetracted] = useState(false)

    const windowHeight = env.OS === 'win32' ? 55 : 29

    const isOpen = Boolean(
        isPresenting ||
            isScreenRecording ||
            remoteRecordingPeer ||
            remoteRecorderPeer ||
            sessionRecordingPeer ||
            debugDisplay
    )

    const handleEndPresentation = () => {
        onEndPresentation && onEndPresentation()
        logUserActivityWithContext(AnalyticsEvents.CLICKED_STOP_MY_VIDEO_BUTTON)
    }

    const handleEndScreenRecording = () => {
        onEndScreenRecording && onEndScreenRecording()
        logUserActivityWithContext(AnalyticsEvents.CLICKED_STOP_SCREEN_RECORDING_BUTTON)
    }

    const onDocumentHandler = (document: Document) => {
        document.body.style.margin = '0px 0px'
        document.body.style.display = 'flex'
        document.body.style.justifyContent = 'center'
        document.body.style.alignItems = 'center'
    }

    const numCards = useMemo(() => {
        let ct = 0
        if (isPresenting || debugDisplay) ct++
        if (isScreenRecording || debugDisplay) ct++
        if (isDesktopBeingControlled || debugDisplay) ct++
        if (remoteRecordingPeer || debugDisplay) ct++
        if (remoteRecorderPeer || debugDisplay) ct++
        if (sessionRecordingPeer || debugDisplay) ct++
        return ct
    }, [
        isPresenting,
        isScreenRecording,
        isDesktopBeingControlled,
        remoteRecordingPeer,
        remoteRecorderPeer,
        sessionRecordingPeer,
    ])

    const { measuredDivRef } = useFloatingStatusBarWindowFit(numCards, isRetracted)

    if (!isOpen) return null

    /**
     * isPresenting
     * isScreenRecording
     * isDesktopBeingControlled
     * remoteRecordingPeer
     * remoteRecorderPeer
     * sessionRecordingPeer
     */
    return (
        <PortalModal
            frameName="FloatingTopStatusBar"
            optionsString={`width=400,height=${windowHeight}`}
            onDocument={onDocumentHandler}
            onClose={() => onClose && onClose()}
        >
            <div className="w-full h-full overflow-hidden">
                <div
                    ref={measuredDivRef}
                    className="flex w-full h-full gap-1"
                    style={{
                        // Force the container to expand to its intrinsic width
                        width: 'max-content',
                        minWidth: 'fit-content',
                    }}
                >
                    {isRetracted ? (
                        <InternalCard className="rounded-none pr-4 pl-1 app-window-drag-region">
                            <div className="app-window-drag-region pl-4 pr-8">
                                <DragHandleIcon className="text-black" />
                            </div>
                            <InternalButton onClick={() => setIsRetracted(false)}>
                                <CaretRightIcon className="text-black" />
                            </InternalButton>
                            <div style={{ backgroundColor: '#f9cc04', width: 16, borderRadius: 16 }}>
                                <div
                                    className="font-bold text-xs"
                                    style={{ color: 'black', width: 16, textAlign: 'center' }}
                                >
                                    {numCards}
                                </div>
                            </div>
                        </InternalCard>
                    ) : (
                        <>
                            <InternalCard className="w-[80px] rounded-[16px] px-4 app-window-drag-region">
                                <div>
                                    <DragHandleIcon className="text-black" />
                                </div>
                                <InternalButton onClick={() => setIsRetracted(true)}>
                                    <CaretLeftIcon className="text-black" />
                                </InternalButton>
                            </InternalCard>

                            {(isPresenting || debugDisplay) && <PresentingCard onStopClicked={handleEndPresentation} />}

                            {(isScreenRecording || debugDisplay) && (
                                <ScreenRecordingCard onStopClicked={handleEndScreenRecording} />
                            )}

                            {(isDesktopBeingControlled || debugDisplay) && (
                                <DesktopControllingCard
                                    onStopClicked={() => RemoteDesktopControlPermissions.revoke()}
                                />
                            )}

                            {/* "... is recording you" */}
                            {(remoteRecorderPeer || debugDisplay) && (
                                <RemoteRecorderPeerCard remoteRecorderPeer={remoteRecorderPeer ?? 'unknown'} />
                            )}

                            {/* "You are recording ..." */}
                            {(remoteRecordingPeer || debugDisplay) && (
                                <RemoteRecordingPeerCard
                                    remoteRecordingPeer={remoteRecordingPeer ?? 'unknown'}
                                    onStopClicked={() => onEndRemoteRecording && onEndRemoteRecording()}
                                />
                            )}

                            {/* session recording */}
                            {(sessionRecordingPeer || debugDisplay) && (
                                <SessionRecordingCard sessionRecordingPeer={sessionRecordingPeer ?? 'unknown'} />
                            )}
                        </>
                    )}
                </div>
            </div>
        </PortalModal>
    )
}
